import { useCallback, useEffect, useRef, useState } from 'react';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import { isRecord } from '../common/is-record';
import { getGeneratedCacheKey } from '../core/cache-key';
import { replaceGeneratedOutput } from '../core/fetch-generated';
import type { FetchFn, GeneratedRequest } from '../core/types';
import type { PromptOfTheDayPrompt, PromptOfTheDayRequest } from '../types';
import {
	fetchPromptOfTheDay,
	PROMPT_OF_THE_DAY_OPTIONS,
	PROMPT_OF_THE_DAY_SLUG,
	refreshPromptOfTheDay,
} from './client';
import { parsePromptsFromOutput } from './prompt-data';
import { regenerateOnce } from './regeneration-guard';

// `stale`: the last stored suggestions, shown because regenerating them failed.
export type PromptOfTheDayWithFallbackStatus = 'loading' | 'ready' | 'stale' | 'unavailable';

export interface PromptOfTheDayWithFallbackOptions {
	/** Defaults to `globalThis.fetch`. */
	fetchFn?: FetchFn;
	// Keep the entry fresh until this long after it was generated, even past its expiry. Can only
	// extend its life: a shorter value is answered from the cache until the expiry anyway.
	maxAgeMs?: number;
	// Called with the number of suggestions each time a generate request this hook waited on succeeds.
	onGenerated?: (promptCount: number) => void;
	// Called each time a generate request this hook waited on fails: `stale` when the last stored
	// suggestions are kept, `none` when there is nothing to fall back on.
	onFailed?: (fallback: 'stale' | 'none') => void;
}

export interface UsePromptOfTheDayWithFallbackResult {
	prompts: readonly PromptOfTheDayPrompt[];
	status: PromptOfTheDayWithFallbackStatus;
	// Removes a used suggestion from the shared entry. Removing the last one asks for new ones and
	// saves them for the next mount (not again on this page after a failure); nothing shown changes.
	removePrompt: (prompt: PromptOfTheDayPrompt) => void;
}

interface StoredPrompts {
	prompts: readonly PromptOfTheDayPrompt[];
	output: unknown;
	expiresAt: number;
	generatedAt?: number;
}

interface State {
	prompts: readonly PromptOfTheDayPrompt[];
	status: PromptOfTheDayWithFallbackStatus;
}

const NO_PROMPTS: readonly PromptOfTheDayPrompt[] = [];
const LOADING: State = { prompts: NO_PROMPTS, status: 'loading' };
const UNAVAILABLE: State = { prompts: NO_PROMPTS, status: 'unavailable' };

const toRequest = (accountId: string, argsJson: string): GeneratedRequest => ({
	slug: PROMPT_OF_THE_DAY_SLUG,
	accountId,
	args: JSON.parse(argsJson) as Record<string, unknown>,
});

// Reads the shared entry from browser storage even once it has expired. An empty list counts:
// every suggestion in it was used.
const readStoredPrompts = (request: GeneratedRequest): StoredPrompts | null => {
	try {
		const stored: unknown = JSON.parse(
			AtlBrowserStorageLocal.getItem(getGeneratedCacheKey(request)) ?? 'null',
		);
		if (!isRecord(stored) || typeof stored.expiresAt !== 'number') {
			return null;
		}

		const output = stored.value;
		const isEmptyList =
			isRecord(output) && Array.isArray(output.prompts) && output.prompts.length === 0;
		const prompts = isEmptyList ? NO_PROMPTS : parsePromptsFromOutput(output);
		if (prompts === null) {
			return null;
		}

		return {
			prompts,
			output,
			expiresAt: stored.expiresAt,
			...(typeof stored.generatedAt === 'number' && { generatedAt: stored.generatedAt }),
		};
	} catch {
		return null;
	}
};

// The suggestions in storage right now, expired or not; `null` when nothing is stored (storage
// blocked, or the write has not landed yet), unlike `[]`, which means every one was used.
export const readStoredPromptsOfTheDay: ({
	accountId,
	args,
}: PromptOfTheDayRequest) => readonly PromptOfTheDayPrompt[] | null = ({ accountId, args }) =>
	readStoredPrompts(toRequest(accountId, JSON.stringify(args ?? {})))?.prompts ?? null;

const isFresh = (
	{ expiresAt, generatedAt }: StoredPrompts,
	maxAgeMs: number | undefined,
	now: number,
): boolean =>
	maxAgeMs !== undefined && generatedAt !== undefined
		? generatedAt + maxAgeMs > now
		: expiresAt > now;

// Today's suggestions, falling back to the last stored ones while they regenerate or if that
// fails. Uses the same shared entry as `usePromptOfTheDay`; nothing is read during server rendering.
export const usePromptOfTheDayWithFallback = (
	{ accountId, args }: PromptOfTheDayRequest,
	{ fetchFn, maxAgeMs, onGenerated, onFailed }: PromptOfTheDayWithFallbackOptions = {},
): UsePromptOfTheDayWithFallbackResult => {
	const [state, setState] = useState<State>(LOADING);

	// A caller writing `args={{}}` inline hands us a new object every render.
	const argsJson = JSON.stringify(args ?? {});

	// Held in a ref, not depended on: callers may build `fetchFn` or `onGenerated` inline.
	const optionsRef = useRef({ fetchFn, maxAgeMs, onGenerated, onFailed });
	useEffect(() => {
		optionsRef.current = { fetchFn, maxAgeMs, onGenerated, onFailed };
	});

	useEffect(() => {
		let isCurrent = true;
		const request = toRequest(accountId, argsJson);
		const options = optionsRef.current;

		const stored = readStoredPrompts(request);
		if (stored !== null && isFresh(stored, options.maxAgeMs, Date.now())) {
			// Fresh but used up: regenerate now rather than wait for the entry to expire.
			const regeneration =
				stored.prompts.length > 0
					? null
					: regenerateOnce(getGeneratedCacheKey(request), () =>
							refreshPromptOfTheDay(
								{ accountId: request.accountId, args: request.args },
								{ fetchFn: options.fetchFn },
							),
						);
			if (regeneration === null) {
				setState({ prompts: stored.prompts, status: 'ready' });
				return;
			}
			setState(LOADING);
			void regeneration.then((prompts) => {
				if (isCurrent) {
					setState(prompts !== null ? { prompts, status: 'ready' } : UNAVAILABLE);
					if (prompts !== null) {
						optionsRef.current.onGenerated?.(prompts.length);
					} else {
						optionsRef.current.onFailed?.('none');
					}
				}
			});
			return () => {
				isCurrent = false;
			};
		}

		setState({ prompts: stored?.prompts ?? NO_PROMPTS, status: 'loading' });
		void fetchPromptOfTheDay(
			{ accountId: request.accountId, args: request.args },
			{ fetchFn: options.fetchFn },
		).then((prompts) => {
			if (!isCurrent) {
				return;
			}
			if (prompts !== null) {
				setState({ prompts, status: 'ready' });
				optionsRef.current.onGenerated?.(prompts.length);
				return;
			}
			// Read again: a suggestion may have been removed while the request was open.
			const fallback = readStoredPrompts(request);
			setState(fallback !== null ? { prompts: fallback.prompts, status: 'stale' } : UNAVAILABLE);
			optionsRef.current.onFailed?.(fallback !== null ? 'stale' : 'none');
		});

		return () => {
			isCurrent = false;
		};
	}, [accountId, argsJson]);

	const removePrompt = useCallback(
		({ shortPrompt }: PromptOfTheDayPrompt) => {
			const isKept = (prompt: PromptOfTheDayPrompt) => prompt.shortPrompt !== shortPrompt;
			setState((current) => ({ ...current, prompts: current.prompts.filter(isKept) }));

			const request = toRequest(accountId, argsJson);
			const stored = readStoredPrompts(request);
			if (stored === null || !isRecord(stored.output)) {
				return;
			}

			const kept = stored.prompts.filter(isKept);
			replaceGeneratedOutput(
				request,
				PROMPT_OF_THE_DAY_OPTIONS.parse,
				{ ...stored.output, prompts: kept },
				{
					expiresAt: stored.expiresAt,
					...(stored.generatedAt !== undefined && { generatedAt: stored.generatedAt }),
				},
			);

			// Every suggestion is used: ask for new ones now, even though the entry has not expired. The
			// answer only lands in the shared entry, so the next mount shows it.
			if (kept.length > 0) {
				return;
			}
			void regenerateOnce(getGeneratedCacheKey(request), () =>
				refreshPromptOfTheDay(
					{ accountId: request.accountId, args: request.args },
					{ fetchFn: optionsRef.current.fetchFn },
				),
			)?.then((prompts) => {
				if (prompts !== null) {
					optionsRef.current.onGenerated?.(prompts.length);
				}
			});
		},
		[accountId, argsJson],
	);

	return { ...state, removePrompt };
};
